import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/use-organization';
import { 
  getBeneficiaryCompetitions,
  getUserCompetitionHabitTallies,
  logCompetitionHabit, 
  unlogCompetitionHabit, 
  isCompetitionHabitLoggedToday 
} from '@/lib/firebase/queries/competitions';
import type { Competition } from '@/types/competitions';
import type { BeneficiaryCompetition } from '@/lib/competitions/beneficiaryCompetitions';
import { notifyCompetitionLogChanged } from '@/lib/competitions/competitionLogEvents';
import { formatCompetitionDateRange } from '@/lib/competitions/standingsView';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface CompetitionHabitsProps {
  selectedDate?: Date;
}

export function CompetitionHabits({ selectedDate = new Date() }: CompetitionHabitsProps) {
  const [competitions, setCompetitions] = useState<BeneficiaryCompetition<Competition>[]>([]);
  // Ended competitions are read-only: per-habit day counts instead of log buttons.
  const [tallies, setTallies] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [habitCompletionStatus, setHabitCompletionStatus] = useState<Record<string, boolean>>({});
  const [loggingHabits, setLoggingHabits] = useState<Set<string>>(new Set());
  
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { selectedOrganization } = useOrganization();

  const fetchCompetitions = async () => {
    if (!currentUser?.uid || !selectedOrganization?.id) return;

    try {
      setLoading(true);
      setCompetitions(await getBeneficiaryCompetitions(selectedOrganization.id));
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkHabitCompletionStatus = async () => {
    if (!currentUser?.uid || competitions.length === 0) return;

    const statusMap: Record<string, boolean> = {};
    const tallyMap: Record<string, number> = {};

    await Promise.all(competitions.filter(c => c.hasEnded).map(async (competition) => {
      const perHabit = await getUserCompetitionHabitTallies(competition.id, currentUser.uid, competition);
      competition.habits.forEach(habit => { tallyMap[`${competition.id}_${habit.id}`] = perHabit[habit.id] || 0; });
    }));
    setTallies(tallyMap);

    await Promise.all(
      competitions.filter(c => !c.hasEnded).flatMap(competition =>
        competition.habits.map(async (habit) => {
          const key = `${competition.id}_${habit.id}`;
          const isCompleted = await isCompetitionHabitLoggedToday(
            competition.id,
            habit.id,
            currentUser.uid
          );
          statusMap[key] = isCompleted;
        })
      )
    );

    setHabitCompletionStatus(statusMap);
  };

  useEffect(() => {
    fetchCompetitions();
  }, [currentUser?.uid, selectedOrganization?.id]);

  useEffect(() => {
    checkHabitCompletionStatus();
  }, [competitions, selectedDate]);

  const handleHabitToggle = async (
    competition: Competition,
    habitId: string,
    habitName: string,
    habitCategory: string,
    isCompleted: boolean
  ) => {
    if (!currentUser?.uid) return;

    const key = `${competition.id}_${habitId}`;
    if (loggingHabits.has(key)) return;
    setLoggingHabits(prev => new Set(prev).add(key));

    try {
      if (isCompleted) {
        await unlogCompetitionHabit(competition.id, habitId, currentUser.uid);
        toast.success(t('dashboard.competition.unlogged'));
      } else {
        await logCompetitionHabit(
          competition.id,
          habitId,
          currentUser.uid,
          competition.organizationId,
          habitName,
          habitCategory,
          'manual'
        );
        toast.success(t('habits.notifications.loggedSuccess'));
      }

      // Update local state
      setHabitCompletionStatus(prev => ({
        ...prev,
        [key]: !isCompleted
      }));
    } catch (error) {
      if (error instanceof Error && error.message.includes('already logged')) {
        // Already logged (e.g. from the phone): that is the state asked for.
        setHabitCompletionStatus(prev => ({ ...prev, [key]: true }));
      } else {
        console.error('Error toggling competition habit:', error);
        toast.error(t('dashboard.competition.updateError'));
      }
    } finally {
      // Rankings re-read their standings after every toggle (namat#36).
      notifyCompetitionLogChanged(competition.id);
      setLoggingHabits(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      Exercises: 'bg-blue-100 text-blue-800',
      Nutrition: 'bg-green-100 text-green-800',
      Sleep: 'bg-purple-100 text-purple-800',
      Stress: 'bg-orange-100 text-orange-800',
      Relations: 'bg-pink-100 text-pink-800',
      Addictions: 'bg-red-100 text-red-800',
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const categoryLabel = (category: string) => {
    const key = `habits.categories.${category}`;
    const label = t(key);
    return label === key ? category : label;
  };

  const isFutureDate = selectedDate > new Date();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-6 w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (competitions.length === 0) {
    return null; // Don't show anything if no active competitions
  }

  return (
    <div className="space-y-4">
      {competitions.map((competition) => (
        <Card key={competition.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              <span>{competition.name}</span>
              <Badge variant="secondary" className={cn('ml-auto', competition.hasEnded && 'bg-muted text-muted-foreground')}>
                {competition.hasEnded ? t('dashboard.competition.ended') : t('dashboard.competition.active')}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>
                {formatCompetitionDateRange(competition.startDate, competition.endDate, language)}
              </span>
            </div>
            {competition.hasEnded && (
              <p className="text-sm text-muted-foreground">{t('dashboard.competition.endedNote')}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {competition.habits.map((habit) => {
                const key = `${competition.id}_${habit.id}`;
                const isCompleted = habitCompletionStatus[key] || false;
                const isLogging = loggingHabits.has(key);

                return (
                  <motion.div
                    key={habit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Badge className={getCategoryColor(habit.category)}>
                        {categoryLabel(habit.category)}
                      </Badge>
                      <div>
                        <h4 className="font-medium">{habit.name}</h4>
                        {habit.description && (
                          <p className="text-sm text-muted-foreground">
                            {habit.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {competition.hasEnded ? (
                      <Badge variant="outline" className="min-w-[100px] justify-center gap-1">
                        <CheckCircle className="h-3 w-3 text-[#7dda55]" />
                        {t('dashboard.competition.daysCompleted', { count: tallies[key] || 0 })}
                      </Badge>
                    ) : (
                    <Button
                      variant={isCompleted ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleHabitToggle(
                        competition,
                        habit.id,
                        habit.name,
                        habit.category,
                        isCompleted
                      )}
                      disabled={isFutureDate || isLogging}
                      className={cn(
                        "min-w-[100px]",
                        isCompleted && "bg-[#7dda55] hover:bg-[#6dc447] text-white"
                      )}
                    >
                      {isLogging ? (
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          <span className="text-xs">{t('dashboard.competition.updating')}</span>
                        </div>
                      ) : isCompleted ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-3 w-3" />
                          <span className="text-xs">{t('habits.loggedOnDate')}</span>
                        </div>
                      ) : isFutureDate ? (
                        <span className="text-xs">{t('habits.notifications.cannotLogFuture')}</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Target className="h-3 w-3" />
                          <span className="text-xs">{t('habits.logForDate')}</span>
                        </div>
                      )}
                    </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
